import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProfile } from "@/contexts/ProfileContext";
import { motion, AnimatePresence } from "framer-motion";
import BasicInfo from "@/pages/paipan/components/BasicInfo";
import ProfessionalPaipan from "@/pages/paipan/components/ProfessionalPaipan";
import ReportPanel from "./ReportPanel";
import type { CardData, BirthFormData, GeneratedBaziResult, ReportState } from "../types";
import { buildExperienceBaziResult, buildProfileFromForm } from "../utils";
import { track } from "@/utils/track";
import { loadBaziFormCache, saveBaziFormCache } from "@/utils/baziFormCache";
import { AI_REQUEST_BLOCKED, reduceAiRequestGate } from "@/utils/aiBaziReportRequest";
import { createGenerateLock } from "./generateLock";

interface DualPaneViewProps {
  data: CardData;
  isLight: boolean;
}

export default function DualPaneView({ data, isLight }: DualPaneViewProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { currentProfile } = useProfile();
  const [reportState, setReportState] = useState<ReportState>("idle");
  const [formMessage, setFormMessage] = useState("");
  const [generatedResult, setGeneratedResult] = useState<GeneratedBaziResult | null>(null);
  const [aiRequestGate, setAiRequestGate] = useState(AI_REQUEST_BLOCKED);
  const [activeTab, setActiveTab] = useState<"report" | "chart">("report");
  const [isMobile, setIsMobile] = useState(false);
  const swipeRef = useRef<HTMLDivElement>(null);
  const isProgramScroll = useRef(false);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generateLockRef = useRef(createGenerateLock());

  const [formData, setFormData] = useState<BirthFormData>(() => loadBaziFormCache() ?? {
    name: "",
    gender: "male",
    birthTime: "",
    birthPlace: "",
    currentResidence: "",
  });
  const [chartTab, setChartTab] = useState<"basic" | "professional">("professional");
  const isDarkTheme = !isLight;

  useEffect(() => {
    const state = location.state as { selectedProfileId?: number } | null;
    if (!state?.selectedProfileId || !currentProfile) return;
    setFormData({
      name: currentProfile.name || "",
      gender: currentProfile.gender === "female" ? "female" : "male",
      birthTime: (currentProfile.birthTime || "").replace(" ", "T"),
      birthPlace: currentProfile.birthPlace || "",
      currentResidence: currentProfile.currentResidence || "",
    });
    setFormMessage("已切换命主，资料已自动填入");
  }, [location.state, currentProfile]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const startGenerate = async () => {
    if (reportState === "generating") return;

    if (!formData.name.trim()) {
      setFormMessage("请先填写姓名");
      return;
    }
    if (!formData.birthTime) {
      setFormMessage("请先选择出生时间");
      return;
    }
    if (!formData.birthPlace) {
      setFormMessage("请先选择出生地区");
      return;
    }
    if (!generateLockRef.current.tryStart()) return;

    setFormMessage("");
    setReportState("generating");
    setAiRequestGate((gate) => reduceAiRequestGate(gate, { type: "reset" }));
    const temporaryProfile = buildProfileFromForm(formData);
    let result: GeneratedBaziResult | null = null;
    try {
      result = await buildExperienceBaziResult(temporaryProfile, i18n.language);
    } catch (err) {
      console.error("排盘失败:", err);
      generateLockRef.current.release();
      setReportState("idle");
      setAiRequestGate((gate) => reduceAiRequestGate(gate, { type: "reset" }));
      setFormMessage("排盘计算失败，请检查出生时间和出生地区后重试");
      return;
    }
    // 排盘成功：缓存表单数据（命主信息）作为下次默认值
    saveBaziFormCache(formData);

    setGeneratedResult(result);
    setAiRequestGate((gate) => reduceAiRequestGate(gate, { type: "paipan-succeeded" }));
    setChartTab("professional");
    track("tool_use", { toolId: "bazi" });

    setTimeout(() => {
      setReportState("done");
      generateLockRef.current.release();
    }, 2500);
  };

  const resetGenerate = () => {
    generateLockRef.current.release();
    setReportState("idle");
    setGeneratedResult(null);
    setAiRequestGate((gate) => reduceAiRequestGate(gate, { type: "reset" }));
    setChartTab("professional");
  };

  const openProfileSelector = () => {
    navigate("/profile-manage?returnTo=/experience%2Fbazi");
  };

  const handleTabClick = (tab: "report" | "chart") => {
    if (!swipeRef.current) return;
    setActiveTab(tab);
    isProgramScroll.current = true;
    const el = swipeRef.current;
    el.scrollTo({
      left: tab === "report" ? 0 : el.scrollWidth / 2,
      behavior: "smooth",
    });
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      isProgramScroll.current = false;
    }, 350);
  };

  const handleScroll = () => {
    if (!isMobile || !swipeRef.current || isProgramScroll.current) return;
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      if (!swipeRef.current) return;
      const el = swipeRef.current;
      const mid = el.scrollWidth / 4;
      setActiveTab(el.scrollLeft < mid ? "report" : "chart");
    }, 80);
  };

  const statusBadge =
    reportState === "idle" ? null : reportState === "generating" ? (
      <span
        className={`text-[10px] md:text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 ${
          isLight
            ? "bg-[#e5e5ea] text-[#6e6e73]"
            : "bg-white/10 text-silver-500"
        }`}
      >
        <i className="ri-loader-4-line animate-spin text-[10px] md:text-[10px]" />
        报告生成中
      </span>
    ) : (
      <span
        className={`text-[10px] md:text-[10px] px-2 py-0.5 rounded-full ${
          isLight
            ? "bg-green-100 text-green-700"
            : "bg-green-500/20 text-green-400"
        }`}
      >
        报告已生成
      </span>
    );

  const ChartInner =
    reportState === "done" && generatedResult ? (
      <div
        className={`h-full min-h-[520px] overflow-hidden flex flex-col ${
          isMobile ? "" : isLight ? "bg-[#f5f5f7]" : "bg-cosmic-950"
        }`}
      >
        <div className={`shrink-0 px-5 pt-4 pb-3 ${isMobile ? "" : isLight ? "bg-[#f5f5f7]" : "bg-cosmic-950"}`}>
          <div
            className={`grid grid-cols-2 rounded-full border p-1 ${
              isLight ? "bg-white border-[#e5e5ea]" : "bg-white/5 border-white/10"
            }`}
          >
            {(
              [
                ["basic", "基本资讯"],
                ["professional", "专业排盘"],
              ] as const
            ).map(([key, label]) => {
              const active = chartTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setChartTab(key)}
                  className={`rounded-full px-3 py-2 text-sm md:text-xs font-medium transition-colors cursor-pointer ${
                    active
                      ? isLight
                        ? "bg-[#4a4a4c] text-white"
                        : "bg-gold-500 text-cosmic-950"
                      : isLight
                        ? "text-[#6e6e73] hover:bg-[#f5f5f7]"
                        : "text-silver-400 hover:bg-white/5"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-auto px-4 py-3">
          {chartTab === "basic" ? (
            <div className="min-w-[360px]">
              <BasicInfo
                data={generatedResult.paipanData}
                pillars={generatedResult.detailData.professionalPillars}
                fourPillars={generatedResult.detailData.fourPillars}
                 isDark={isDarkTheme}
                 showAiReport={false}
                 detailData={generatedResult.detailData}
               />
            </div>
          ) : (
            <div className="w-full">
              <ProfessionalPaipan
                pillars={generatedResult.detailData.professionalPillars}
                daYun={generatedResult.detailData.professionalDaYun}
                liuNian={generatedResult.detailData.professionalLiuNian}
                qiYunInfo={generatedResult.detailData.qiYunInfo}
                jiaoYunInfo={generatedResult.detailData.jiaoYunInfo}
                currentAge={generatedResult.currentAge}
                siLing={generatedResult.siLing}
                mingGong={generatedResult.detailData.professionalMingGong}
                isDark={isDarkTheme}
                birthTime={generatedResult.birthTime}
                birthPlace={generatedResult.profile.birthPlace}
                gender={generatedResult.profile.gender}
                currentYear={generatedResult.currentYear}
                startAge={generatedResult.detailData.startAge}
                startMonths={generatedResult.detailData.startMonths}
                isForward={generatedResult.detailData.isForward}
                preciseJiaoYear={generatedResult.detailData.preciseJiaoYear}
                preciseJiaoMonth={generatedResult.detailData.preciseJiaoMonth}
                fourPillars={generatedResult.detailData.fourPillars}
                shenshaItems={generatedResult.detailData.shensha}
                tableShenshaItems={generatedResult.detailData.tableShensha}
                yuelingData={generatedResult.detailData.yueling}
                 xiaoyunItems={generatedResult.detailData.xiaoyun}
                geju={generatedResult.serverPro?.geju}
                xiyong={generatedResult.serverPro?.xiyong}
                rizhu={generatedResult.serverPro?.rizhu}
              />
            </div>
          )}
        </div>
      </div>
    ) : (
      <div className="relative h-full min-h-[360px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${data.bgImage}')` }}
        />
        <div
          className={`absolute inset-0 ${
            isLight
              ? "bg-gradient-to-br from-white/70 via-white/50 to-white/70"
              : "bg-gradient-to-br from-cosmic-900/70 via-cosmic-950/60 to-cosmic-900/70"
          }`}
        />
        <div className="relative h-full flex flex-col items-center justify-center p-6">
          <div
            className={`w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center mb-4 ${
              reportState === "done"
                ? isLight
                  ? "bg-[#4a4a4c] border-2 border-[#4a4a4c]/30"
                  : "bg-gold-500/20 border-2 border-gold-500/30"
                : reportState === "generating"
                  ? isLight
                    ? "bg-[#4a4a4c]/10 border-2 border-[#4a4a4c]/20"
                    : "bg-gold-500/10 border-2 border-gold-500/20"
                  : isLight
                    ? "bg-[#4a4a4c]/10 border-2 border-[#4a4a4c]/20"
                    : "bg-gold-500/10 border-2 border-gold-500/20"
            }`}
          >
            {reportState === "done" ? (
              <i
                className={`ri-check-line text-3xl md:text-4xl ${
                  isLight ? "text-[#4a4a4c]" : "text-gold-400"
                }`}
              />
            ) : reportState === "generating" ? (
              <i
                className={`ri-loader-4-line text-3xl md:text-4xl animate-spin ${
                  isLight ? "text-[#4a4a4c]" : "text-gold-400"
                }`}
              />
            ) : (
              <i
                className={`ri-pie-chart-line text-3xl md:text-4xl ${
                  isLight ? "text-[#4a4a4c]/40" : "text-gold-500/40"
                }`}
              />
            )}
          </div>
          <p
            className={`text-base md:text-sm font-medium mb-1 text-center ${
              isLight ? "text-[#1c1c1e]" : "text-white"
            }`}
          >
            {reportState === "done"
              ? "排盘已生成"
              : reportState === "generating"
                ? "正在生成命盘..."
                : "排盘图形化展示"}
          </p>
          <p
            className={`text-sm md:text-xs text-center max-w-[200px] ${
              isLight ? "text-[#9e9ea2]" : "text-silver-400"
            }`}
          >
            {reportState === "done"
              ? "命盘已排好，可查看右侧图示"
              : reportState === "generating"
                ? "系统正在根据您提供的出生信息生成命盘，请稍候..."
                : `点击「${data.button}」生成后，右侧将显示完整命盘图示`}
          </p>
        </div>
      </div>
    );

  if (isMobile) {
    return (
      <motion.div
        key={data.title}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full flex-1 flex flex-col min-h-0"
      >
        {/* Mobile Tab Bar */}
        <div
          className={`shrink-0 flex border-b ${
            isLight
              ? "border-[#e5e5ea] bg-white"
              : "border-white/10 bg-cosmic-900"
          }`}
        >
          {(["report", "chart"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabClick(tab)}
              className={`flex-1 py-3 text-base md:text-sm font-medium text-center relative cursor-pointer transition-colors ${
                activeTab === tab
                  ? isLight
                    ? "text-[#4a4a4c]"
                    : "text-white"
                  : isLight
                    ? "text-[#9e9ea2]"
                    : "text-silver-400"
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <i
                  className={`${
                    tab === "report"
                      ? "ri-file-text-line"
                      : "ri-pie-chart-line"
                  } text-base md:text-sm`}
                />
                {tab === "report" ? "排盘报告" : "命盘图示"}
              </span>
              {activeTab === tab && (
                <motion.div
                  layoutId="mobile-tab-indicator"
                  className={`absolute bottom-0 left-3 right-3 h-[2px] rounded-full ${
                    isLight ? "bg-[#4a4a4c]" : "bg-gold-500"
                  }`}
                />
              )}
            </button>
          ))}
        </div>

        {/* Swipeable content area */}
        <div
          ref={swipeRef}
          onScroll={handleScroll}
          className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden snap-x snap-mandatory flex scroll-smooth"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {/* Report Panel */}
          <div className="w-full flex-shrink-0 snap-start overflow-y-auto px-3 py-3 space-y-2">
            <ReportPanel
              data={data}
              isLight={isLight}
              isDarkTheme={isDarkTheme}
              reportState={reportState}
              formData={formData}
              setFormData={setFormData}
              formMessage={formMessage}
              generatedResult={generatedResult}
              openProfileSelector={openProfileSelector}
              aiRequestGate={aiRequestGate}
              onStartGenerate={startGenerate}
              onResetGenerate={resetGenerate}
            />
          </div>

          {/* Chart Panel */}
          <div className="w-full flex-shrink-0 snap-start overflow-y-auto">
            <div
              className={`px-4 py-3 border-b flex items-center justify-between ${
                isLight
                  ? "border-[#e5e5ea] bg-transparent"
                  : "border-white/10 bg-transparent"
              }`}
            >
              <div className="flex items-center gap-2">
                <i
                  className={`ri-pie-chart-line text-base md:text-sm ${
                    isLight ? "text-[#6e6e73]" : "text-silver-500"
                  }`}
                />
                <span
                  className={`text-base md:text-sm font-medium ${
                    isLight ? "text-[#1c1c1e]" : "text-white"
                  }`}
                >
                  命盘图示
                </span>
              </div>
              <span
                className={`text-[10px] md:text-[10px] px-2 py-0.5 rounded-full ${
                  isLight
                    ? "bg-[#e5e5ea] text-[#6e6e73]"
                    : "bg-white/10 text-silver-500"
                }`}
              >
                实时预览
              </span>
            </div>
            {ChartInner}
          </div>
        </div>
      </motion.div>
    );
  }

  // PC: dual column layout
  return (
    <motion.div
      key={data.title}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="w-full flex-1 min-h-0 flex flex-col lg:flex-row gap-4 lg:gap-5"
    >
      {/* Left panel: Report */}
      <div
        className={`flex-1 min-w-0 rounded-2xl border flex flex-col ${
          isLight
            ? "bg-white border-[#e5e5ea]"
            : "bg-cosmic-900 border-white/10"
        } ${reportState === "done" ? "" : "overflow-hidden"}`}
      >
        <div
          className={`shrink-0 px-5 py-3.5 border-b flex items-center gap-3 ${
            isLight
              ? "border-[#e5e5ea] bg-[#f5f5f7]"
              : "border-white/10 bg-cosmic-900"
          }`}
        >
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              isLight ? "bg-[#4a4a4c]" : "bg-gold-500/20"
            }`}
          >
            <i className="ri-sparkling-line text-base text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              className={`text-base font-bold truncate ${
                isLight ? "text-[#1c1c1e]" : "text-white"
              }`}
            >
              {data.title}
            </h2>
            <p
              className={`text-sm truncate ${
                isLight ? "text-[#9e9ea2]" : "text-silver-400"
              }`}
            >
              {data.subTitle}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {statusBadge}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 lg:p-8 space-y-5">
          <ReportPanel
            data={data}
            isLight={isLight}
            isDarkTheme={isDarkTheme}
            reportState={reportState}
            formData={formData}
            setFormData={setFormData}
            formMessage={formMessage}
            generatedResult={generatedResult}
            openProfileSelector={openProfileSelector}
            aiRequestGate={aiRequestGate}
            onStartGenerate={startGenerate}
            onResetGenerate={resetGenerate}
          />
        </div>
      </div>

      {/* Right panel: Chart */}
      <div
        className={`lg:w-[420px] xl:w-[480px] shrink-0 rounded-2xl border overflow-hidden flex flex-col relative ${
          isLight
            ? "bg-white border-[#e5e5ea]"
            : "bg-cosmic-900 border-white/10"
        }`}
      >
        <div
          className={`shrink-0 px-5 py-3.5 border-b flex items-center justify-between ${
            isLight
              ? "border-[#e5e5ea] bg-[#f5f5f7]"
              : "border-white/10 bg-cosmic-900"
          }`}
        >
          <div className="flex items-center gap-2">
            <i
              className={`ri-pie-chart-line text-base ${
                isLight ? "text-[#6e6e73]" : "text-silver-500"
              }`}
            />
            <span
              className={`text-base font-medium ${
                isLight ? "text-[#1c1c1e]" : "text-white"
              }`}
            >
              命盘图示
            </span>
          </div>
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full ${
              isLight
                ? "bg-[#e5e5ea] text-[#6e6e73]"
                : "bg-white/10 text-silver-300"
            }`}
          >
            实时预览
          </span>
        </div>

        <div className="flex-1 relative overflow-hidden">{ChartInner}</div>
      </div>
    </motion.div>
  );
}
